import {Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, IconButton, TableCell, Tooltip, Typography} from "@mui/material";
import TableRow from "@mui/material/TableRow";
import React, {useEffect, useRef} from "react";
import {Delete, HighlightOff, PriorityHigh} from "@mui/icons-material";
import {DescriptionColumn} from "../../taxonomy_columns";
import EnhancedTable from "../../../../../../../components/table";
import CatalogManagerTable from "../../../../../../../components/CatalogManagerTable";
import CatalogFilters, {addFiltersToTable} from "../../../../../../../components/CatalogFilters/catalog_filters";
import EnhancedSelect from "../../../../../../../components/EnhancedSelect";
import AttributeDialog from "../AttributeDalog/AttributeDialog";
import SettingsTableMoreOpts from "../../taxonomy_table_more_opts";
import {getAttributes as utilGetAttributes} from "../../../../../../../utils/utils";
import Auth from "../../../../../../../auth";
import { tableRowSx, tableCellTruncateSx, pageContentGap, DialogHeader } from "../../../../../../../theme";

interface AttributeTableProps {
    attributeSet: any;
    allAttributes: any[];
    updateAttributeSet: (attributeSet?: any) => void;
    setAttributeSet: (attributeSet: any) => void;
    history?: any;
    location?: any;
    loading?: boolean;
    table: any;
    config: any;
    setConfig: (updater: (prevState: any) => any) => void;
    setCatalog?: (updater: (prevState: any) => any) => void;
}

interface AttributeSetTableViewProps {
    updateAttributeSet: (attributeSet?: any) => void;
    setAttributeSet: (attributeSet: any) => void;
    attributeSet: any;
    setError: (error: any) => void;
    error?: any;
    history?: any;
    location?: any;
}

const baseConfig = {
    selectable: false,
    order: 'asc',
    orderBy: 'Name',
    pageSize: 25,
    pageSizes: [25, 50, 100],
    refresh: false,
    term: "",
    facets: [],
    filter: null,
    filterInUrl: false
}


const iconButtonSx = {
    width: "100%",
    justifyContent: "flex-start",
    padding: "12px",
    textTransform: "none"
};

const iconContainerStyle = {
    display: "flex",
    gap: pageContentGap,
    alignItems: "center",
    width: "160px"
};

function AttributeTable(props: AttributeTableProps) {
    const hasPermission = Auth.hasPermission("settings/manage");
    const {attributeSet, allAttributes} = props;

    // Used for editing an attribute in place //
    const [attributeDialog, setAttributeDialog] = React.useState<{open: boolean; isEdit: boolean; previousAttribute: any}>({
        open: false, isEdit: true, previousAttribute: null
    });
    const [newAttributeDialog, setNewAttributeDialog] = React.useState(false);
    const [existingAttributeDialog, setExistingAttributeDialog] = React.useState(false);

    const [existingAttributes, setExistingAttributes] = React.useState<any[]>([]);
    const generateRows = () => {
        return props.table.data.map((row: any, rowIndex: number) => {
            return (
                <TableRow key={rowIndex} sx={tableRowSx}
                          data-testid={`poc-attribute-set-member-row-${row.Name}`}
                          style={{cursor: "pointer"}}
                          onClick={() => {
                              if (row.error)
                                  setAttributeDialog({
                                      open: true,
                                      isEdit: false,
                                      previousAttribute: row
                                  });
                              else
                                  setAttributeDialog({
                                      open: true,
                                      isEdit: true,
                                      previousAttribute: row
                                  });
                          }}
                >
                    {props.config.columns.map((column: any, colIndex: number) => {
                        if (props.table.extensions.isColumnHidden(column)) return null;
                        if (column.hidden && !column.toggleable) return null;

                        let content = column.template
                            ? column.template(row, column, colIndex, rowIndex,
                                (filter: any) => addFiltersToTable([filter], props.table))
                            : (<>{row[column.id]}</>);

                        return (
                            <TableCell key={`${rowIndex}-${colIndex}`}
                                       data-testid={column.id ? `poc-attribute-set-member-${column.id}-${row.Name}` : undefined}
                                       style={{
                                           width: column.width,
                                           minWidth: column.width,
                                           maxWidth: column.width,
                                           padding: column.padding,
                                       }}
                            >
                                {content}
                            </TableCell>
                        )
                    })}
                    <TableCell style={{width: 0, padding: "4px 0", position: "relative"}}>
                        <SettingsTableMoreOpts
                            setCatalog={props.setCatalog}
                            currentSetting={"attributes"}

                            row={row}

                            disallowDelete={true}
                            customActions={
                                ({open, setOpen}) => (
                                    <>
                                        {!hasPermission ? <></> : (
                                            <Button sx={iconButtonSx}
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        e.preventDefault();
                                                        props.setAttributeSet({
                                                            ...attributeSet,
                                                            Attributes: attributeSet.Attributes.filter((attribute: string) => attribute !== row.Name)
                                                        })
                                                        setOpen();
                                                    }}
                                            >
                                                <div style={iconContainerStyle}>
                                                    <Delete style={{width: "20px", height: "20px"}}/>
                                                    <Typography>Remove from set</Typography>
                                                </div>
                                            </Button>
                                        )}
                                    </>
                                )
                            }

                            attributeDialog={attributeDialog} setAttributeDialog={setAttributeDialog}
                        />
                    </TableCell>
                    <TableCell sx={tableCellTruncateSx} style={{width: 0, padding: "4px 20px 4px 0"}}>
                        {row.error && (
                            // The tooltip text is only in the DOM on hover, so E2E asserts on this
                            // testid instead of the "Attribute not found!" string.
                            <Grid container justifyContent={"center"} alignItems={"center"}
                                  data-testid={`poc-attribute-set-member-error-${row.Name}`}>
                                <Tooltip title={row.error}>
                                    <PriorityHigh style={{color: "#f44336"}}/>
                                </Tooltip>
                            </Grid>
                        )}
                    </TableCell>
                </TableRow>
            );
        });
    };

    return (
        <>
            <Grid container style={{width: "100%"}}>
                <Grid item container style={{marginBottom: "5px", width: "100%"}}>
                    <CatalogFilters
                        style={{
                            flex: 0.5,
                            padding: 0
                        }}
                        table={props.table}
                        config={props.config} setConfig={props.setConfig}
                        history={props.history} location={props.location}
                        disableDetailedFilters={true}
                        disableColumnSelector={true}
                        disableTermInUrl={true}
                    />

                    <Grid item container justifyContent={"flex-end"} style={{flex: 0.5, gap: "6px"}}>
                        <Button variant="text" disabled={props.attributeSet.System || !hasPermission} onClick={() => setExistingAttributeDialog(true)}>
                            Add Existing Attribute(s)
                        </Button>
                        <Button variant="text" color="primary" disabled={props.attributeSet.System || !hasPermission} onClick={() => setNewAttributeDialog(true)}>
                            Add New Attribute(s)
                        </Button>
                    </Grid>
                </Grid>

                <Grid item container style={{flexDirection: "column", width: "100%"}}>
                    <CatalogManagerTable
                        containerStyle={{borderRadius: 0, maxHeight: "340px"}}
                        stickyHeader={true}
                        table={props.table}
                        config={props.config}
                        generateRows={generateRows}
                        disableLoadingPlaceholder={true}
                        hasInlineFilters={true}
                    />
                </Grid>
            </Grid>
            {/*region Dialogs*/}
            <Dialog open={existingAttributeDialog} onClose={() => setExistingAttributeDialog(false)}
                    maxWidth={"lg"} slotProps={{
                paper: {style: {borderRadius: "12px", minHeight: "100px", maxHeight: "80vh"}}
            }}
            >
                <DialogTitle>
                    <Grid item container justifyContent={"space-between"} alignItems={"center"}>
                        <Grid item container style={{flex: 0.5}}>
                            <DialogHeader>
                                Add existing attributes
                            </DialogHeader>
                        </Grid>

                        <IconButton onClick={() => setExistingAttributeDialog(false)} size="large">
                            <HighlightOff style={{color: "red"}}/>
                        </IconButton>
                    </Grid>
                </DialogTitle>

                <DialogContent>
                    <Grid container style={{width: "600px", gap: pageContentGap, flexDirection: "column"}}>
                        <EnhancedSelect label={"Attributes To Add"}
                                        isMulti loading={props.loading}
                                        isClearable
                                        options={allAttributes.filter((attribute) => !attributeSet.Attributes.includes(attribute.Name)).map((attribute) => ({
                                            label: attribute.FriendlyName,
                                            value: attribute.Name,
                                            ...attribute
                                        }))}
                                        value={existingAttributes.map(attribute => {
                                            return {
                                                label: attribute.FriendlyName,
                                                value: attribute.Name,
                                                ...attribute
                                            }
                                        })}
                                        onChange={(e: any) => {
                                            setExistingAttributes(e ? [...e] : [])
                                        }}
                                        selectorStyle={{height: "100%"}}
                                        containerStyle={{flex: 0.5}}
                        />
                    </Grid>
                </DialogContent>

                <DialogActions style={{padding: "12px 24px 24px 24px"}}>
                    <Grid item container justifyContent={"flex-end"} style={{gap: "8px"}}>
                        <Button variant="text" onClick={() => {
                            setExistingAttributeDialog(false);
                            setExistingAttributes([]);
                        }}>
                            Cancel
                        </Button>

                        <Button variant="text" color="primary"
                                onClick={() => {
                                    props.setAttributeSet({
                                        ...attributeSet,
                                        Attributes: [
                                            ...attributeSet.Attributes,
                                            ...existingAttributes.map((attribute) => attribute.Name)
                                        ]
                                    });
                                    setExistingAttributeDialog(false);
                                    setExistingAttributes([]);
                                }}
                        >
                            Add
                        </Button>
                    </Grid>
                </DialogActions>
            </Dialog>
            {/*Edit/Create Existing in Set*/}
            <AttributeDialog
                setConfig={props.setConfig}
                outsideEditing={true}
                isEdit={attributeDialog.isEdit}
                open={attributeDialog.open}
                // AttributeDialog and SettingsDialog both close with setOpen(false). Passing the
                // raw setter replaced this state object with a bare boolean, wiping isEdit and
                // previousAttribute along with it; only the falsy `open` read made that look fine.
                setOpen={(value: any) => setAttributeDialog((prevState) => ({
                    ...prevState,
                    open: value === true
                }))}
                previousAttribute={attributeDialog.previousAttribute}
                onComplete={(savedAttribute: any, runStatus: any) => {
                    if (runStatus && runStatus.errorMessage) return;

                    const previousName = attributeDialog.previousAttribute && attributeDialog.previousAttribute.Name;
                    if (!previousName || previousName === savedAttribute.Name) return;

                    // A rename rewrites this set's member list on the server, but nothing refetches
                    // the set, so Attributes here still holds the old name. Mirror the rename
                    // locally or the table looks up a name that no longer exists and flags the row
                    // "Attribute not found!" until the page is reloaded.
                    props.setAttributeSet({
                        ...attributeSet,
                        Attributes: attributeSet.Attributes.map((attribute: string) =>
                            attribute === previousName ? savedAttribute.Name : attribute
                        )
                    });
                }}
            />
            {/*New Attribute*/}
            <AttributeDialog
                setConfig={props.setConfig}
                open={newAttributeDialog}
                setOpen={setNewAttributeDialog}
                outsideEditing={true}
                onComplete={(newAttribute: any, runStatus: any) => {
                    if (runStatus.errorMessage) return;

                    props.setAttributeSet({
                        ...attributeSet,
                        Attributes: [
                            ...attributeSet.Attributes,
                            newAttribute.Name
                        ]
                    })
                }}
            />
            {/*endregion*/}
        </>
    );
}

export default function AttributeSetTableView(props: AttributeSetTableViewProps) {
    const {updateAttributeSet, setAttributeSet, attributeSet, setError} = props;
    const [loading, setLoading] = React.useState(false);

    //region Functions
    // Callers that have just refetched pass the fresh list in. setAvailableAttributes is async
    // and availableAttributesRef is only reassigned during render, so resolving against the ref
    // right after a refetch reads the list from one render ago and reports every just-renamed
    // member as missing.
    const mapAttributes = (attributes?: any[]) => {
        const availableAttributes = attributes ?? availableAttributesRef.current ?? [];

        let currentAttributes = attributeSetRef.current.Attributes.map((attribute: any) => {
            let foundAttribute = availableAttributes.find((a: any) => a.Name === attribute);
            if (foundAttribute === undefined)
                return {
                    Name: attribute,
                    FriendlyName: "",
                    Group: "",
                    Type: "",
                    Description: "",
                    IncludedInSet: true,
                    error: "Attribute not found!"
                }
            // Copy rather than stamp IncludedInSet onto the shared available-attributes object,
            // which also feeds the "Add Existing Attribute(s)" picker.
            return {...foundAttribute, IncludedInSet: true};
        });

        return currentAttributes;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    };
    const getAttributes = async () => {
        setLoading(true);
        const attributes = await utilGetAttributes(undefined, true);
        if (attributes.errorMessage) {
            // Read through a ref: getData is frozen in state at first render, so a direct call
            // would keep invoking the first render's setError for the life of the dialog.
            setErrorRef.current(attributes);
            setLoading(false);
            // Keep the last known list so a failed refetch does not blank every row.
            return availableAttributesRef.current ?? [];
        }

        setAvailableAttributes(attributes);
        setLoading(false);
        return attributes;
    }
    //endregion

    let attributeConfig = {
        ...baseConfig,
        title: `attributes`,
        orderBy: "IncludedInSet",
        order: "desc",
        getData: async () => {
            const attributes = await getAttributes();
            let data = mapAttributes(attributes);
            let term = configRef.current.term;

            // Non-odata search term filtering, configurable via the searchable property in the column config //
            if (term !== "" && term !== undefined) {
                let split = term.toLowerCase().split(" ");
                let fields = configRef.current.columns.filter((column: any) => column.searchable).map((c: any) => c.id);
                data = data.filter((r: any) =>
                    split.every((s: string) =>
                        fields.filter((f: string) => r[f] && r[f].toLowerCase().indexOf(s) > -1).length > 0
                    )
                );
            }

            return data;
        },
        columns: [
            {
                id: "Name", label: "Name", toggleable: false, sortable: true, searchable: true, width: "120px"
            },
            {
                id: "FriendlyName",
                label: "FriendlyName",
                toggleable: true,
                sortable: true,
                searchable: true,
                width: "120px"
            },
            {
                id: "Group", label: "Group", toggleable: true, sortable: true, searchable: true, width: "50px"
            },
            {
                id: "Type", label: "Type", toggleable: true, sortable: true, width: "50px"
            },
            {
                id: "Description", label: "Description", toggleable: true,
                template: (row: any, column: any, colIndex: number, rowIndex: number, addFilter: any) => <DescriptionColumn row={row} column={column}
                                                                                             colIndex={colIndex}
                                                                                             addFilter={addFilter}
                                                                                             rowIndex={rowIndex}/>
            },
            {
                id: "Options", label: "Options", hidden: true,
            },
            {
                id: "System", label: "System", hidden: true,
            },
            {
                id: "LastModified", label: "LastModified", hidden: true,
            },
            {
                id: "Hidden", label: "Hidden", hidden: true,
            },
            {
                id: "HiddenFromPartner", label: "HiddenFromPartner", hidden: true,
            },
            {empty: true, hidden: true}, // Header for action col 1
            {empty: true, hidden: true} // Header for action col 2
        ]
    }

    const [availableAttributes, setAvailableAttributes] = React.useState<any[]>([]);
    const [config, setConfig] = React.useState<any>({...attributeConfig});

    const configRef = useRef<any>(undefined);
    const availableAttributesRef = useRef<any>(undefined);
    const attributeSetRef = useRef<any>(undefined);
    const setErrorRef = useRef(setError);

    availableAttributesRef.current = availableAttributes;
    configRef.current = config;
    setErrorRef.current = setError;
    // Assigned during render like the other two refs. Doing this in the [attributeSet] effect
    // instead left the ref one render behind, so a getData triggered by anything other than that
    // effect's own refresh toggle (paging, sorting, search) mapped the previous member list.
    attributeSetRef.current = attributeSet;

    // No [config] effect refetching attributes: EnhancedTable calls config.getData() on mount and
    // again whenever config.refresh flips, and getData fetches the list itself. A second fetch here
    // raced the first, and whichever response landed last won setAvailableAttributes — so the
    // "Add Existing Attribute(s)" picker could end up rendering an older list than the table.

    useEffect(() => {
        setConfig((prevState: any) => ({...prevState, refresh: !prevState.refresh}));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [attributeSet])

    return (
        <EnhancedTable config={config}
                       history={props.history} location={props.location}
                       template={(table: any) =>
                           <AttributeTable
                               attributeSet={attributeSet} allAttributes={availableAttributes}
                               updateAttributeSet={updateAttributeSet} setAttributeSet={setAttributeSet}
                               history={props.history} location={props.location} loading={loading}
                               table={table} config={config} setConfig={setConfig}
                           />
                       }/>
    )
}
